import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,Button, MenuItem, Typography, Alert, Box } from "@mui/material";
import { useState } from "react";
import ErrorIcon from "@mui/icons-material/Error";



interface Props{
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    // patientId will be auto-generated in backend
    givenName: string;
    familyName: string;
    dateOfBirth?: string | null;
    sex?: string | null;
    notes?: string | null;
  }) => Promise<void>;
}

const sexes = ["female","male","other","unknown"];

export default function PatientFormDialog({ open, onClose, onSubmit }: Props){
  const [form, setForm] = useState({
    // patientId removed; generated by backend
    givenName: "",
    familyName: "",
    dateOfBirth: "",
    sex: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    givenName: "",
    familyName: "",
    dateOfBirth: "",
    general: "",
  });

  const valid = form.givenName.trim() !== "" && form.familyName.trim() !== "" && form.dateOfBirth.trim() !== "";

  const handleClose = () => {
    // Reset form state
    setForm({
      givenName: "",
      familyName: "",
      dateOfBirth: "",
      sex: "",
      notes: ""
    });
    
    // Clear all errors
    setErrors({
      givenName: "",
      familyName: "",
      dateOfBirth: "",
      general: "",
    });
    
    // Reset submitting state
    setSubmitting(false);
    
    // Call parent onClose
    onClose();
  };

  async function handleSubmit() {
    // Clear previous errors
    setErrors({
      givenName: "",
      familyName: "",
      dateOfBirth: "",
      general: "",
    });

    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        givenName: form.givenName.trim(),
        familyName: form.familyName.trim(),
        dateOfBirth: form.dateOfBirth ? new Date(form.dateOfBirth).toISOString() : undefined,
        sex: form.sex || undefined,
        notes: form.notes || undefined
      });
      handleClose(); // Use handleClose instead of onClose to clear errors
    } catch (error) {
      console.error("Failed to create patient:", error);
      
      // Parse error message and set appropriate field errors
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      
      if (errorMessage.includes("already exists")) {
        setErrors(prev => ({
          ...prev,
          general: errorMessage,
        }));
      } else if (errorMessage.includes("Date of birth is required")) {
        setErrors(prev => ({
          ...prev,
          dateOfBirth: errorMessage,
        }));
      } else if (errorMessage.includes("Given name")) {
        setErrors(prev => ({
          ...prev,
          givenName: errorMessage,
        }));
      } else if (errorMessage.includes("Family name")) {
        setErrors(prev => ({
          ...prev,
          familyName: errorMessage,
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          general: errorMessage,
        }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Patient</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={2}>
          {errors.general && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontWeight: 500,
                },
                '& .MuiAlert-icon': {
                  color: 'error.main',
                },
                backgroundColor: '#ffebee',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.15)',
              }}
            >
              {errors.general}
            </Alert>
          )}
          
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Given Name"
              value={form.givenName}
              onChange={(e) => {
                setForm({ ...form, givenName: e.target.value });
                if (errors.givenName) {
                  setErrors({ ...errors, givenName: "", general: "" });
                }
              }}
              required
              fullWidth
              disabled={submitting}
              error={!!errors.givenName}
              helperText={errors.givenName}
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }
              }}
            />
            <TextField
              label="Family Name"
              value={form.familyName}
              onChange={(e) => {
                setForm({ ...form, familyName: e.target.value });
                if (errors.familyName) {
                  setErrors({ ...errors, familyName: "", general: "" });
                }
              }}
              required
              fullWidth
              disabled={submitting}
              error={!!errors.familyName}
              helperText={errors.familyName}
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }
              }}
            />
          </Stack>
          
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Date of Birth"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth}
              onChange={(e) => {
                setForm({ ...form, dateOfBirth: e.target.value });
                if (errors.dateOfBirth) {
                  setErrors({ ...errors, dateOfBirth: "", general: "" });
                }
              }}
              required
              fullWidth
              disabled={submitting}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              sx={{
                '& .MuiFormHelperText-root.Mui-error': {
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }
              }}
            />
            <TextField
              select
              label="Sex"
              value={form.sex}
              onChange={e => setForm({...form, sex: e.target.value})}
              fullWidth
              disabled={submitting}
            >
              <MenuItem value="">—</MenuItem>
              {sexes.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          
          <TextField
            label="Notes"
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!valid || submitting} variant="contained">
          {submitting ? "Creating..." : "Create Patient"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

